import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ArtistLayout from "@/components/layout/ArtistLayout";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Plus, FileText, Clock, CheckCircle, Edit, Eye, XCircle, Calendar, AlertTriangle, FolderSearch } from "lucide-react";
import { Button } from "@/components/ui/button";

const ArtistDashboard = () => {
  const { token, user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const response = await axios.get(`${API}/artist/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubmissions(response.data.filter(s => !s.archived));
      } catch (error) {
        console.error("Failed to fetch submissions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [token]);

  const getStatusBadge = (status) => {
    const styles = {
      draft: "bg-gray-500/20 text-gray-400",
      submitted: "bg-orange-500/20 text-orange-400",
      pending_approval: "bg-orange-500/20 text-orange-400",
      pending_agency_approval: "bg-orange-500/20 text-orange-400",
      pending_brand_approval: "bg-purple-500/20 text-purple-400",
      approved: "bg-green-500/20 text-green-400",
      rejected: "bg-[#ED5A34]/20 text-[#ED5A34]"
    };
    const labels = {
      draft: "Draft",
      submitted: "Pending Agency",
      pending_approval: "Pending Agency",
      pending_agency_approval: "Pending Agency",
      pending_brand_approval: "Pending Brand",
      approved: "Approved",
      rejected: "Rejected"
    };
    return (
      <span className={`inline-flex items-center px-3 py-1 text-xs font-semibold uppercase tracking-wider ${styles[status] || styles.draft}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Get deadline status for display
  const getDeadlineInfo = (submission) => {
    if (!submission.submission_deadline) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadline = new Date(submission.submission_deadline);
    deadline.setHours(0, 0, 0, 0);
    
    const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilDeadline < 0) {
      return { status: "overdue", label: `${Math.abs(daysUntilDeadline)} days overdue`, color: "text-red-400" };
    } else if (daysUntilDeadline <= 3) {
      return { status: "due_soon", label: `Due in ${daysUntilDeadline} days`, color: "text-orange-400" };
    } else {
      return { status: "on_track", label: `Due: ${submission.submission_deadline}`, color: "text-gray-400" };
    }
  };

  const hasEmptyDrafts = submissions.some(s => {
    if (s.status !== "draft") return false;
    const empty = (v) => Array.isArray(v) ? v.length === 0 : !v;
    return empty(s.merchandise_url) && empty(s.proof_of_play_url) && empty(s.bottle_console_url) && empty(s.branding_stories_post_url) && !s.instagram_analytics_url;
  });

  return (
    <ArtistLayout>
      <div className="animate-fade-in" data-testid="artist-dashboard">
        {/* Recovery guidance banner for artists with empty drafts */}
        {hasEmptyDrafts && (
          <div className="mb-6 border border-amber-500/40 bg-amber-500/10 p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="recovery-banner">
            <AlertTriangle size={24} className="text-amber-400 shrink-0" />
            <div className="flex-1">
              <p className="text-white font-medium mb-1">Uploaded files but your submission shows as Draft?</p>
              <p className="text-gray-300 text-sm">
                Your files are safe. Go to the <span className="text-amber-300 font-medium">Recovery</span> page, claim your uploaded files, add your venue details, and re-submit — no need to upload anything again.
              </p>
            </div>
            <Link to="/artist/recovery">
              <Button className="bg-amber-500 hover:bg-amber-600 text-black font-semibold flex items-center gap-2" data-testid="recovery-banner-button">
                <FolderSearch size={18} />
                Recover My Files
              </Button>
            </Link>
          </div>
        )}
        {/* Hero Banner */}
        <div 
          className="relative mb-10 p-8 md:p-12 overflow-hidden"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1701460356742-deecc12da361?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjb25jZXJ0JTIwY3Jvd2QlMjByZWQlMjBsaWdodGluZyUyMGF0bW9zcGhlcmV8ZW58MHx8fHwxNzY4OTczMzI1fDA&ixlib=rb-4.1.0&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#00B4E5]/90 to-[#00B4E5]/50" />
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-heading text-white mb-2">
              WELCOME, {user?.name?.toUpperCase()}
            </h1>
            <p className="text-gray-200 mb-6">Submit your campaign details for the Sound of Good Times promotion</p>
            <Link to="/artist/submit">
              <Button className="kf-btn-primary flex items-center gap-2" data-testid="new-submission-button">
                <Plus size={20} />
                New Submission
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 mb-10">
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{submissions.length}</p>
                <p className="stat-label">Total Submissions</p>
              </div>
              <FileText size={32} className="text-gray-600" />
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{submissions.filter(s => s.status === "pending_agency_approval" || s.status === "pending_brand_approval").length}</p>
                <p className="stat-label">Pending Review</p>
              </div>
              <Clock size={32} className="text-[#F58220]" />
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{submissions.filter(s => s.status === "approved").length}</p>
                <p className="stat-label">Approved</p>
              </div>
              <CheckCircle size={32} className="text-green-500" />
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-value">{submissions.filter(s => s.status === "rejected").length}</p>
                <p className="stat-label">Rejected</p>
              </div>
              <XCircle size={32} className="text-[#ED5A34]" />
            </div>
          </div>
        </div>

        {/* Submissions List */}
        <div className="bg-gray-900 border border-gray-800">
          <div className="px-6 py-4 border-b border-gray-800">
            <h2 className="font-heading text-xl text-white">MY SUBMISSIONS</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading submissions...</div>
          ) : submissions.length === 0 ? (
            <div className="p-12 text-center">
              <FileText size={48} className="mx-auto text-gray-700 mb-4" />
              <p className="text-gray-400 mb-4">You haven't made any submissions yet</p>
              <Link to="/artist/submit">
                <Button className="kf-btn-primary">Create Your First Submission</Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {submissions.map((submission) => {
                const deadlineInfo = getDeadlineInfo(submission);
                return (
                  <div 
                    key={submission.id} 
                    className="p-6 hover:bg-gray-800/50 transition-colors"
                    data-testid={`submission-item-${submission.id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium text-white">{submission.venue}</h3>
                          {getStatusBadge(submission.status)}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Event: {submission.date}
                          </span>
                          <span>•</span>
                          <span>{submission.city}</span>
                          <span>•</span>
                          <span>S-User: {submission.s_user_id}</span>
                        </div>
                        {/* Show deadline info */}
                        {deadlineInfo && submission.status !== "approved" && submission.status !== "rejected" && (
                          <div className={`flex items-center gap-1 mt-2 text-sm ${deadlineInfo.color}`}>
                            {deadlineInfo.status === "overdue" || deadlineInfo.status === "due_soon" ? (
                              <AlertTriangle size={14} />
                            ) : (
                              <Clock size={14} />
                            )}
                            <span>Submission Deadline: {deadlineInfo.label}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {submission.status === "draft" ? (
                          <Link to={`/artist/submit/${submission.id}/media`}>
                            <Button variant="outline" size="sm" className="border-gray-700 text-white hover:bg-gray-800">
                              <Edit size={16} className="mr-1" />
                              Continue
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/artist/submission/${submission.id}`}>
                            <Button variant="outline" size="sm" className="border-gray-700 text-white hover:bg-gray-800">
                              <Eye size={16} className="mr-1" />
                              View
                            </Button>
                          </Link>
                        )}
                        <Link to={`/artist/submission/${submission.id}/edit`}>
                          <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                            Edit
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </ArtistLayout>
  );
};

export default ArtistDashboard;
